import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronsUpDown } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { filterAccounts, getSwitchableAccounts } from "@/lib/account-switcher";
import { getSidebarNavGroups, getRoleLabel, type NavItem, type SystemRole } from "@/lib/rbac";
import { usePendingMembers } from "@/hooks/use-users";
import { useConnectorCount } from "@/hooks/use-connectors";
import { useResolvedEntitlements } from "@/hooks/use-entitlements";
import type { EntitlementKey } from "@/lib/entitlements/modules";
import { Icon } from "@/pages/prototype-icons";
import { Input } from "@/components/ui/input";
import { sidebarTestIds as ids } from "@/testids/sidebarTestIds";
import { topNavTestIds } from "@/testids/topNavTestIds";
import {
  ActivityIcon,
  AlertIcon,
  AppsIcon,
  BellIcon,
  BroadcastIcon,
  ChevronDownIcon,
  FastnMark,
  GearIcon,
  GitBranchIcon,
  GraphIcon,
  HomeIcon,
  ListIcon,
  OrganizationIcon,
  PlayIcon,
  PlugIcon,
  SidebarCollapseIcon,
  SparkleFillIcon,
  StackIcon,
  ContainerIcon,
  CreditCardIcon,
  DatabaseIcon,
  KeyIcon,
  LockIcon,
  LogIcon,
  PeopleIcon,
  ShieldCheckIcon,
  ShieldIcon,
  SlidersIcon,
  SyncIcon,
  TableIcon,
  ToolsIcon,
  TrashIcon,
  WorkflowIcon,
  ZapIcon,
} from "./sidebar-icons";
import "./sidebar.css";

/** Octicon per destination, matching the prototype's choices. Keyed by label so
 *  the nav model stays free of presentation. */
const ICONS: Record<string, React.ComponentType> = {
  Home: HomeIcon,
  Integrations: PlugIcon,
  Widgets: AppsIcon,
  Activity: ActivityIcon,
  Customers: OrganizationIcon,
  Reporting: GraphIcon,
  Settings: GearIcon,
  Agent: SparkleFillIcon,
  Connectors: StackIcon,
  "Unified APIs": TableIcon,
  Connections: BroadcastIcon,
  Workflows: WorkflowIcon,
  Triggers: ZapIcon,
  // Same bell the page's own header carries. Without an entry here it fell
  // through to HomeIcon, so the rail showed a house next to "Pending updates".
  "Pending updates": BellIcon,
  Events: ListIcon,
  Traces: GitBranchIcon,
  Alerts: AlertIcon,
  Executions: PlayIcon,
  "Sync Reports": SyncIcon,
  // Settings children. Without these every one fell through to HomeIcon, so
  // the rail showed a column of identical house glyphs. Slugs are the
  // prototype's own choices.
  People: PeopleIcon,
  General: ToolsIcon,
  "API keys": KeyIcon,
  // Same plug as the Connect button in the top bar and on Home: this page is
  // that dialog, and a settings entry with no entry here silently falls back
  // to the house glyph.
  "MCP server": PlugIcon,
  Secrets: LockIcon,
  Environments: ContainerIcon,
  Configs: SlidersIcon,
  Database: DatabaseIcon,
  "OAuth apps": ShieldCheckIcon,
  "SaaS Connectors": StackIcon,
  Billing: CreditCardIcon,
  "Audit log": LogIcon,
  Trash: TrashIcon,
  Roles: ShieldIcon,
  Tenants: OrganizationIcon,
};

const COLLAPSED_KEY = "fastn-sidebar-collapsed";

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}

export function Sidebar(): React.ReactElement {
  const location = useLocation();
  const { user, userContext, memberships } = useAuth();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Collapsing happens with the pointer still over the rail, and the
  // hover-flyout rule would keep it visually expanded until the pointer left —
  // reading as "the button did nothing". The lock suppresses the flyout until
  // the pointer leaves once, so the rail shrinks the instant it is clicked.
  // Cleared on enter as well as leave: the rail shrinks out from under a
  // stationary pointer, and a browser that skips the resulting mouseleave would
  // otherwise strand the lock — with the toggle itself hidden while collapsed,
  // that would leave no way to reopen the rail.
  const [hoverLocked, setHoverLocked] = useState(false);
  const [openBranches, setOpenBranches] = useState<Record<string, boolean>>({});
  // Accordion: at most one group expanded, so two expanded groups can no longer
  const role: SystemRole = userContext?.role ?? "end_user";
  const groups = getSidebarNavGroups(role);
  const { data: pendingMembers = [] } = usePendingMembers();
  // Real catalog size, as the prototype shows beside Integrations. Activity
  // carries no count: nothing cheap answers "events today" without a new
  // endpoint, and a decorative number would be a lie.
  //
  // A COUNT, not the catalog. The Sidebar is on every page, so reaching for
  // useConnectors() here downloaded all 373 connectors in full detail (612 kB on
  // prod) to render one integer, on every page load in the dashboard.
  const { data: connectorCount = 0 } = useConnectorCount();
  // A nav entry that leads to a page the org is not entitled to is the same
  // dead end as a button that 403s. Hidden while the query is in flight, to
  // match the hide-by-default policy in useEntitlement.
  const { data: entitlements } = useResolvedEntitlements();
  const isEntitled = (item: { entitlement?: EntitlementKey }): boolean =>
    !item.entitlement || entitlements?.[item.entitlement] === true;

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch { /* private mode */ }
  }, [collapsed]);

  /**
   * Settings resolves to a role-specific first page and Customers also lives
   * under /settings, so Customers is matched exactly and Settings claims the
   * rest. Children carry their tab in the query string.
   */
  /**
   * `exact` distinguishes a leaf from a section parent. Without it every
   * /settings child matched the parent's prefix rule at once, so all twelve
   * lit up together and read as one solid highlighted block with no current
   * item — which is what the Settings sub-nav was doing.
   */
  const isActive = (path: string, exact = false) => {
    const here = location.pathname;
    if (path.includes("?")) {
      const [base, query] = path.split("?");
      const tab = new URLSearchParams(query).get("tab");
      if (here === base) {
        if (location.search.includes(query)) return true;
        // The first tab owns the bare URL: /integrations with no ?tab IS the
        // connectors list, so its rail item must read as current there.
        return tab === "connectors" && !location.search.includes("tab=");
      }
      // Detail routes live under the tab's subtree
      // (/integrations/connectors/:slug), which the query match never saw —
      // so opening a connector un-highlighted the whole rail.
      return !!tab && here.startsWith(`${base}/${tab}`);
    }
    if (path === "/") return here === "/";
    // Customers is a leaf that owns a subtree (/settings/customers/:id).
    // Handled here so its rail item and the Settings-parent exclusion below
    // agree on the boundary.
    if (path === "/settings/customers") return here === path || here.startsWith(path + "/");
    if (exact) return here === path;
    // Section parents claim their whole subtree. Customers lives under
    // /settings but is a sibling of Settings in the rail, so it is excluded.
    if (path.startsWith("/settings")) {
      // Whole subtree, not the exact path: /settings/customers/<id> is still
      // the Customers section, and must not light Settings up instead.
      return here.startsWith("/settings") && !here.startsWith("/settings/customers");
    }
    return here === path || here.startsWith(path + "/");
  };

  /** True while the current route sits anywhere inside this item. */
  const inSection = (item: NavItem) =>
    isActive(item.path) || (item.children ?? []).some((k) => isActive(k.path, true));

  const branchOpen = (item: NavItem) => openBranches[item.path] ?? inSection(item);

  const countFor = (label: string) =>
    label === "Settings" ? pendingMembers.length
      : label === "Integrations" ? connectorCount
      : 0;

  // "Organisation", not "Workspace": this is the org the rail's switcher below
  // calls one ("Switch organisation"), and the pane it links to titles
  // "Organisation". Naming it a workspace here made the placeholder the only
  // surface calling that object a third thing.
  const accountName = memberships.find((m) => m.orgId === userContext?.orgId)?.accountName
    ?? userContext?.accountName ?? "Organisation";

  const renderCount = (label: string, count: number) => count > 0 && (
    <span
      className="navCount"
      data-testid={label === "Settings" ? "settings-pending-badge" : ids.count(label)}
    >
      {count}
    </span>
  );

  return (
    <aside
      className={cn("fx-rail", "hidden md:flex")}
      data-collapsed={collapsed ? "1" : "0"}
      data-hover-lock={hoverLocked ? "1" : "0"}
      onMouseEnter={() => setHoverLocked(false)}
      onMouseLeave={() => setHoverLocked(false)}
      data-testid={ids.sidebar}
    >
      {/* The top of the rail says which product you are in, which never changes;
          the organisation you are acting as lives at the foot, where switching
          it has somewhere to live. Putting the customer's name up here made the
          product look like it belonged to them. */}
      <div className="wsHead">
        {/* Link, not the collapse button's sibling <span>s: the product mark at
            the top of a rail is expected to return you to the top of the
            product, and as a real <a href> it also cmd-clicks and honours the
            router basename under the `/gcp/` mount (CLAUDE.md rule 18). */}
        <Link to="/" className="wsBrand" aria-label="fastn home" data-testid={ids.brandLink}>
          <span className="wsMark"><FastnMark /></span>
          <span className="wsName" data-testid={ids.workspaceName}>fastn</span>
        </Link>
        <button
          type="button"
          className="wsBtn"
          onClick={() => {
            setHoverLocked(!collapsed);
            setCollapsed((c) => !c);
          }}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          data-testid={ids.collapseButton}
        >
          <SidebarCollapseIcon expanded={!collapsed} />
        </button>
      </div>

      {/* `.topbar-nav` is retained per apps/dashboard/CLAUDE.md rule 5 — the QA
          role-visibility specs select on it, and the nav moving from the top
          bar to the rail must not silently disable them.

          Everything in here that changes the route is a <Link>, so it renders a
          real <a href>: middle-click, cmd-click and "open in new tab" work, and
          the accessibility tree calls it a link rather than a button. They were
          buttons calling navigate(), which looks identical and behaves like
          neither. The genuine controls below — the group headers and the branch
          caret — stay buttons, because they toggle disclosure and go nowhere.

          Link honours the router basename, so these stay correct under the
          `/gcp/` mount (CLAUDE.md rule 18); a hand-written href would not. */}
      <div className={cn("navScroll", "topbar-nav sidebar-nav-rail")} data-testid={ids.nav}>
        {groups.map((group, gi) => {
          // Categories are always expanded — the accordion (only the active
          // group open) hid the others and read as "collapsed".
          const open = true;
          return (
            <div className="navGroup" data-open={open ? 1 : 0} key={group.label ?? `g${gi}`}>
              {group.label && (
                <div className="navGroupHead" data-testid={ids.groupLabel(group.label)}>
                  {group.label}
                </div>
              )}
              <div className="navItems">
                {group.items.filter(isEntitled).map((item) => {
                  const Icon = ICONS[item.label] ?? HomeIcon;
                  const kids = (item.children ?? []).filter(isEntitled);
                  const count = countFor(item.label);

                  if (kids.length === 0) {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="navItem"
                        aria-current={isActive(item.path) ? "page" : "false"}
                        data-testid={ids.link(item.label)}
                      >
                        <Icon />
                        <span>{item.label}</span>
                        {renderCount(item.label, count)}
                      </Link>
                    );
                  }

                  const bOpen = branchOpen(item);
                  // Land on the first child the role can open, the way Settings
                  // already resolves to its first available page. Navigating to
                  // the bare parent path left every child unmatched, so the
                  // section opened with no page highlighted.
                  const firstChild = kids[0];
                  return (
                    <div className="navBranch" data-open={bOpen ? 1 : 0} key={item.path}>
                      <div className="navRow">
                        {/* Points at the child it actually opens, not the bare
                            parent path, so cmd-click lands on the same page a
                            plain click does. */}
                        <Link
                          to={firstChild ? firstChild.path : item.path}
                          className="navItem navItem--parent"
                          data-section-active={inSection(item) ? 1 : 0}
                          aria-current="false"
                          onClick={() => setOpenBranches((p) => ({ ...p, [item.path]: true }))}
                          data-testid={ids.link(item.label)}
                        >
                          <Icon />
                          <span>{item.label}</span>
                          {renderCount(item.label, count)}
                        </Link>
                        <button
                          type="button"
                          className="navCaret"
                          onClick={() => setOpenBranches((p) => ({ ...p, [item.path]: !bOpen }))}
                          aria-expanded={bOpen}
                          aria-label={`${item.label} pages`}
                          data-testid={ids.expandButton(item.label)}
                        >
                          <ChevronDownIcon />
                        </button>
                      </div>
                      <div className="navSubs">
                        {kids.map((kid) => {
                          const KidIcon = ICONS[kid.label] ?? HomeIcon;
                          return (
                            <Link
                              key={kid.path}
                              to={kid.path}
                              className="navSub"
                              aria-current={isActive(kid.path, true) ? "page" : "false"}
                              data-testid={ids.link(kid.label)}
                            >
                              <KidIcon />
                              <span>{kid.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="railFoot">
        <RailAccountCard orgName={accountName} userName={user?.name} email={user?.email} />
      </div>
    </aside>
  );
}

/** Below this many organisations the list is scannable at a glance, so a search
 *  field would be noise; above it, filtering is faster than scrolling. */
const SEARCH_THRESHOLD = 2;

/** Two-letter monogram for an organisation, the way the design identifies one. */
function orgInitials(name: string): string {
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase().slice(0, 2) || "O";
}

/** The rail's single account card: who you are signed in as, in which
 *  organisation and role, and the control that switches organisation. One card
 *  instead of the previous org-switcher + profile-link pair, which repeated the
 *  same identity twice and read as duplication.
 *
 *  Both predecessors' click contracts survive: the card body (`.topbar-user`)
 *  still links straight to the profile — Profile owns settings and also offers
 *  sign-out — and the switch button (`.account-switcher-trigger`) opens this
 *  card's account menu: who you are signed in as, the organisation listbox, and
 *  Sign out. Those selectors, `.account-switcher-dropdown`, and the topNav
 *  testids are on the E2E preserve list in apps/dashboard/CLAUDE.md rule 5.
 *  Role rather than plan sits on each menu row: the question you ask before
 *  switching is what you will be allowed to do once you land. */
function RailAccountCard({ orgName, userName, email }: {
  orgName: string;
  userName?: string | null;
  email?: string | null;
}): React.ReactElement {
  const { userContext, memberships, switchAccount, logout, canSwitchPlatformOrg } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The org id, not a boolean: the row that was clicked has to be able to show
  // its own spinner, and a boolean cannot say which row that is.
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Off by default on Live; the platform (org_platform) account only appears in
  // the switcher when the logged-in admin has been toggled on from the admin
  // console.
  const options = getSwitchableAccounts(memberships, canSwitchPlatformOrg);
  const current = memberships.find((m) => m.orgId === userContext?.orgId);
  const role = getRoleLabel(current?.role ?? userContext?.role ?? "end_user");
  const initials = (userName || email || "?").slice(0, 2).toUpperCase();

  const showSearch = options.length > SEARCH_THRESHOLD;
  const visible = filterAccounts(options, query);

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    if (showSearch) searchRef.current?.focus();
  }, [open, showSearch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = async (orgId: string) => {
    setSwitchingOrgId(orgId);
    try {
      await switchAccount(orgId);
      setOpen(false);
      navigate("/");
    } finally {
      setSwitchingOrgId(null);
    }
  };

  return (
    <div className="orgWrap" ref={ref}>
      {open && (
        <div
          className={cn("orgMenu", "account-switcher-dropdown")}
          data-testid={topNavTestIds.accountSwitcherMenu}
        >
          {/* The menu is opened from the account card and carries Sign out, so
              it has to read as this account's menu. Titled only "Switch
              organisation", sign-out sat under a heading nobody looking for it
              would open — which is what QA reported. */}
          <div className="orgIdent" data-testid={topNavTestIds.accountMenuIdentity}>
            <span className="orgAv orgAv--sm">{initials}</span>
            <span className="orgIdentBody">
              <span className="orgOptName">{userName || email || "Account"}</span>
              {email && userName && <span className="orgOptMeta">{email}</span>}
            </span>
          </div>
          <div className="orgMenuRule" />
          <div className="orgMenuT">Switch organisation</div>
          {showSearch && (
            <div className="px-1 pb-1">
              <Input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search organisations"
                aria-label="Search organisations"
                className="h-8"
                data-testid={topNavTestIds.accountSwitcherSearchInput}
              />
            </div>
          )}
          <div
            className="max-h-60 overflow-y-auto overflow-x-hidden"
            role="listbox"
            aria-label="Switch organisation"
            aria-busy={switchingOrgId !== null}
            data-testid={topNavTestIds.accountSwitcherList}
          >
            {visible.map((m) => {
              const label = m.accountName || m.accountSlug;
              const isCurrent = m.id === current?.id;
              const isSwitching = switchingOrgId === m.orgId;
              return (
                <button
                  key={m.id}
                  type="button"
                  className="orgOpt"
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => !isCurrent && pick(m.orgId)}
                  disabled={switchingOrgId !== null || isCurrent}
                  data-switching={isSwitching || undefined}
                  data-testid={topNavTestIds.accountSwitcherOption(m.orgId)}
                >
                  <span className="orgAv orgAv--sm">{orgInitials(label)}</span>
                  <span className="orgOptBody">
                    <span className="orgOptName">{label}</span>
                    {/* Replaces the role, rather than sitting beside it: the
                        role is not what someone is waiting to read here. */}
                    <span className="orgOptMeta">
                      {isSwitching ? "Switching…" : getRoleLabel(m.role)}
                    </span>
                  </span>
                  {isSwitching
                    ? <span className="orgOptSpin" data-testid={topNavTestIds.accountSwitcherSpinner} />
                    : isCurrent && <span className="orgTick"><Icon name="check" /></span>}
                </button>
              );
            })}
            {visible.length === 0 && (
              <p
                className="px-2 py-3 text-xs text-fg-muted"
                data-testid={topNavTestIds.accountSwitcherEmpty}
              >
                {query.trim()
                  ? `No organisations match "${query.trim()}".`
                  : "No other organisations to switch to."}
              </p>
            )}
          </div>
          <div className="orgMenuRule" />
          <button
            type="button"
            className="orgNew"
            onClick={() => { setOpen(false); void logout(); }}
            data-testid={topNavTestIds.accountSignOutButton}
          >
            <Icon name="sign-out" />
            Sign out
          </button>
        </div>
      )}
      <div className="acct">
        <Link
          to="/profile"
          className={cn("acctMain", "topbar-user")}
          aria-label="View profile"
          title={email ? `View profile — ${email}` : "View profile"}
          data-testid={ids.userButton}
        >
          <span className="acctAv">{initials}</span>
          <span className="acctBody">
            <span className="acctName">{userName || email || "Account"}</span>
            <span className="acctMail">
              <span data-testid={ids.orgName}>{orgName}</span> · {role}
            </span>
          </span>
          <ChevronRight size={14} className="acctGo" aria-hidden="true" />
        </Link>
        <button
          type="button"
          className={cn("acctSwitch", "account-switcher-trigger")}
          onClick={() => setOpen((o) => !o)}
          // What opens is a composite — identity block, organisation listbox,
          // Sign out — so "listbox" promised a screen reader the popup itself
          // was the list. The role="listbox" below stays scoped to the org list.
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
          title="Account menu — switch organisation or sign out"
          disabled={switchingOrgId !== null}
          data-testid={topNavTestIds.accountSwitcherButton}
        >
          <ChevronsUpDown size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
